import { setIdHeader } from "@/clients/open_api_injection";
import { UsersService } from "@/clients/services/UsersService";
import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { QueryKeys } from "@/constants/query-keys";
import type { CurrentUser } from "@/hooks/requests/user/use-get-user";
import { UserIdSchema } from "@/schemas/user-schema";
import { markSignedInBefore } from "@/utils/has-signed-in-before";
import { setLocalStorageJson } from "@/utils/local-storage";
import { setMixpanelUser } from "@/utils/mixpanel";
import { setUserInfo } from "@/utils/sentry/tools";
import { setSmartlookUser } from "@/utils/smartlook";
import type { QueryClient } from "@tanstack/react-query";

interface HydrateUserSessionInput {
  userId: string;
  email: string;
  queryClient: QueryClient;
}

/**
 * Re-populates the local `USER_KEY` from the Clerk session + backend when the
 * user is signed in to Clerk but localStorage lost the `user` record (e.g. a
 * partial logout or storage eviction left the Clerk cookie alive but cleared
 * localStorage). Without this, every `guardGetUserFromLocalStorage()` queryFn
 * throws "User not found" with no recovery.
 *
 * This is the same `USER_KEY` write sequence as `processToken` (use-login),
 * extracted so the load-bearing ordering is preserved (write USER_KEY *before*
 * `setQueryData([USER])`, then await the `[SUBSCRIPTION_STATUS]` invalidation —
 * see CLAUDE.md). It is non-destructive: `loginApiV1UsersLoginPost` only returns
 * `{ id, isOnboardingCompleted }`, generates no plan, and mutates nothing.
 */
export const hydrateUserSession = async ({
  userId,
  email,
  queryClient,
}: HydrateUserSessionInput): Promise<void> => {
  setIdHeader(userId);

  const { data } = await UsersService.loginApiV1UsersLoginPost(userId);
  const isOnboardingCompleted = data.isOnboardingCompleted;

  const dataStorage = UserIdSchema.parse({ email, id: userId, isOnboardingCompleted });
  setLocalStorageJson(LocalStorageKeys.USER_KEY, dataStorage);
  markSignedInBefore();

  queryClient.setQueryData<CurrentUser>([QueryKeys.USER], (prev) => ({
    ...(prev ?? {}),
    id: userId,
    isOnboardingCompleted,
  }));

  // USER_KEY is in place now; kick the queries that auto-enabled (and errored)
  // while it was missing. Awaited so the errored SUBSCRIPTION_STATUS query
  // transitions out of its terminal error state.
  await queryClient.invalidateQueries({ queryKey: [QueryKeys.SUBSCRIPTION_STATUS] });
  await queryClient.invalidateQueries({ queryKey: [QueryKeys.USER] });

  setUserInfo(dataStorage);
  await setSmartlookUser(userId, email);
  setMixpanelUser(userId, email);
};
